import { baseApi } from '@/app/baseApi';
import type { Employee, EmployeeListResponse } from './types';

interface ListArgs {
  search?: string;
  department?: string;
  status?: string;
}

export const employeesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listEmployees: builder.query<EmployeeListResponse, ListArgs | void>({
      query: (args) => ({ url: '/employees', params: args ?? {} }),
      providesTags: (result) =>
        result
          ? [
              ...result.employees.map((e) => ({ type: 'Employee' as const, id: e.id })),
              { type: 'Employee' as const, id: 'LIST' },
            ]
          : [{ type: 'Employee' as const, id: 'LIST' }],
    }),

    getEmployee: builder.query<Employee, string>({
      query: (id) => `/employees/${id}`,
      transformResponse: (response: { employee: Employee }) => response.employee,
      providesTags: (_result, _error, id) => [{ type: 'Employee', id }],
    }),

    /** Consent-scoped staff contacts directory. */
    directory: builder.query<EmployeeListResponse, ListArgs | void>({
      query: (args) => ({ url: '/employees/directory', params: args ?? {} }),
      providesTags: [{ type: 'Directory', id: 'LIST' }],
    }),

    departments: builder.query<string[], void>({
      query: () => '/employees/meta/departments',
      transformResponse: (response: { departments: string[] }) => response.departments,
      providesTags: ['Department'],
    }),

    updateEmployee: builder.mutation<Employee, { id: string; changes: Partial<Employee> }>({
      query: ({ id, changes }) => ({ url: `/employees/${id}`, method: 'PATCH', body: changes }),
      transformResponse: (response: { employee: Employee }) => response.employee,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Employee', id },
        { type: 'Employee', id: 'LIST' },
        // Consent changes alter who appears in the directory.
        { type: 'Directory', id: 'LIST' },
      ],
    }),

    createEmployee: builder.mutation<Employee, Partial<Employee>>({
      query: (body) => ({ url: '/employees', method: 'POST', body }),
      transformResponse: (response: { employee: Employee }) => response.employee,
      invalidatesTags: [{ type: 'Employee', id: 'LIST' }, 'Department'],
    }),
  }),
});

export const {
  useListEmployeesQuery,
  useGetEmployeeQuery,
  useDirectoryQuery,
  useDepartmentsQuery,
  useUpdateEmployeeMutation,
  useCreateEmployeeMutation,
} = employeesApi;
